// FLAG:useDocuAi — the Documents variant of the single-file upload chain, extracted from
// fileService.startSingleUpload so its ORDERING is unit-testable without the store, axios or the
// full duck graph. Every dependency is injected.
//
// This is NOT runReceiptSingleUpload. That chain defers `finished` until complete-upload resolved,
// which is correct for receipts because runReceiptUploadBatch settles on classifyBatchUploads.
// Documents uploads are still accounted by runLegacyUploadBatch, whose subscriber only ever looks at
// rows with `finished === true`; deferring `finished` there would turn a failed complete-upload into
// a batch that settles only on the 5-minute timeout. So the ordering below is the LEGACY ordering,
// step for step:
//
//     upload POST → mark the row finished → THEN call complete-upload
//
// What this chain adds on top of that ordering — and the only things it adds:
//   1. the caller wraps both network steps in withUploadRetry, so a transient failure (no response,
//      429, 502/503/504) gets up to MAX_UPLOAD_ATTEMPTS instead of failing terminally;
//   2. `minioId` is recorded on the pending row. That is what lets the flyout's retry button call
//      retryCompleteUpload instead of re-POSTing bytes the DMS already has (pending-upload.onRetry
//      keys on exactly this field, and a legacy row never carried it).
//
// Legacy sources never reach this function; startSingleUpload keeps its original body for them.

export type DocumentSingleUploadDeps = {
  // Sends the bytes. Already wrapped in the retry policy by the caller.
  postFile: () => Promise<{ data?: { result?: { minioId?: string } } }>,
  // Calls POST /api/dms-upload/complete-upload. Already wrapped in the retry policy by the caller.
  // Safe to repeat: the route is idempotent for an already-completed (tenantId, minioId, checksum).
  completeUpload: (args: { minioId: string }) => Promise<unknown>,
  // dispatch(updatePending({ uploadToken, ...patch }))
  patchPending: (patch: Record<string, unknown>) => void,
  // onUploadError(error, { uploadToken })
  reportError: (error: unknown) => void,
}

export const runDocumentSingleUpload = async({
  postFile,
  completeUpload,
  patchPending,
  reportError,
}: DocumentSingleUploadDeps): Promise<void> => {
  let minioId: string | undefined

  try {
    const response = await postFile()
    minioId = response?.data?.result?.minioId
  } catch (error) {
    // Mirrors the legacy special case: some DMS rejections still carry a usable minioId, meaning the
    // object did land. Complete it rather than reporting a failure.
    minioId = (error as { response?: { data?: { minioId?: string } } })?.response?.data?.minioId
    if (!minioId) {
      reportError(error)
      return
    }
  }

  // Legacy ordering: the row is finished as soon as the bytes are in the DMS. `minioId` rides along
  // — the only addition — so a later complete-upload failure leaves a row the retry button can
  // re-COMPLETE rather than re-upload.
  patchPending({
    maxPartNumber: 1,
    finished: true,
    running: false,
    error: null,
    ...(minioId ? { minioId } : {}),
  })

  // No object id in a successful response: legacy did nothing further here, and complete-upload
  // would reject on the missing minioId anyway.
  if (!minioId) {
    return
  }

  try {
    await completeUpload({ minioId })
  } catch (error) {
    reportError(error)
  }
}
